import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Calendar, Clock, ArrowUpRight } from "lucide-react";
import { NEWS } from "@/constants/testIds";
import PromoCountdown from "@/components/PromoCountdown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABEL = {
  actualite: "Actualité",
  promotion: "Promotion",
  avantage: "Avantage",
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

export default function NewsDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/articles/${slug}`)
      .then((res) => !cancelled && setArticle(res.data))
      .catch(() => !cancelled && setError("Article introuvable."))
      .finally(() => !cancelled && setLoading(false));
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="pt-32 pb-24 max-w-3xl mx-auto px-6 text-[#475569]">
        Chargement…
      </div>
    );
  }
  if (error || !article) {
    return (
      <div className="pt-32 pb-24 max-w-3xl mx-auto px-6">
        <p className="text-[#c05c3d] mb-6">{error || "Article introuvable."}</p>
        <button
          onClick={() => navigate("/actualites")}
          className="inline-flex items-center gap-2 border border-[#0a192f]/70 text-[#0a192f] px-6 py-3 hover:bg-[#0a192f] hover:text-[#efeade] transition-colors"
        >
          <ArrowLeft size={16} /> Retour aux actualités
        </button>
      </div>
    );
  }

  const badgeColor =
    article.category === "promotion"
      ? "bg-[#c05c3d] text-[#efeade]"
      : article.category === "avantage"
      ? "bg-[#b08d57] text-[#0a192f]"
      : "bg-[#0a192f] text-[#efeade]";

  return (
    <article data-testid={NEWS.detailRoot} className="bg-[#f7f5f0]">
      {/* Hero image */}
      <div className="relative w-full h-[52vh] min-h-[380px] overflow-hidden">
        <img
          src={article.image_url}
          alt={article.title}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a192f]/85 via-[#0a192f]/25 to-transparent" />
        <div className="relative h-full max-w-4xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-14">
          <span
            className={`self-start text-[10px] tracking-[0.24em] uppercase font-semibold px-3 py-1.5 mb-6 ${badgeColor}`}
          >
            {CATEGORY_LABEL[article.category] || article.category}
          </span>
          <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] text-[#efeade] max-w-3xl">
            {article.title}
          </h1>
          <div className="mt-6 flex items-center gap-6 text-[#efeade]/80 text-sm">
            <span className="inline-flex items-center gap-2">
              <Calendar size={14} strokeWidth={1.5} />
              {formatDate(article.published_at)}
            </span>
            <span className="inline-flex items-center gap-2">
              <Clock size={14} strokeWidth={1.5} />
              {article.read_minutes} min de lecture
            </span>
            <span className="overline text-[#b08d57]">{article.tag}</span>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <Link
          to="/actualites"
          data-testid={NEWS.detailBack}
          className="inline-flex items-center gap-2 text-sm text-[#475569] hover:text-[#c05c3d] transition-colors mb-10"
        >
          <ArrowLeft size={14} strokeWidth={1.5} /> Retour aux actualités
        </Link>

        <p className="font-serif italic text-2xl md:text-3xl leading-snug text-[#0a192f] mb-12">
          {article.excerpt}
        </p>

        {article.category === "promotion" && article.promo_ends_at && (
          <div className="mb-12">
            <PromoCountdown endsAt={article.promo_ends_at} variant="banner" />
          </div>
        )}

        <div className="space-y-6 text-[17px] leading-[1.8] text-[#0f172a]">
          {article.body.split("\n\n").map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-20 border-t border-[#0a192f]/10 pt-10">
          <div className="overline mb-4">Prochaine étape</div>
          <h3 className="font-serif text-3xl text-[#0a192f] leading-tight max-w-xl">
            Parlons de votre situation en 30 minutes.
          </h3>
          <Link
            to="/#contact"
            className="mt-6 group inline-flex items-center gap-3 bg-[#0a192f] text-[#efeade] px-8 py-4 text-sm tracking-wide hover:bg-[#c05c3d] transition-colors"
          >
            Prendre rendez-vous
            <ArrowUpRight
              size={18}
              strokeWidth={1.5}
              className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
            />
          </Link>
        </div>
      </div>
    </article>
  );
}
